import React, { useState, useEffect } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, ChartOptions } from 'chart.js';
import { ArrowDownCircle, ArrowRight, ArrowUpCircle, PiggyBank, UserCheck, Users, Wallet } from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

type User = {
  id: string;
  name: string;
  email: string;
  number: string;
  total_deposit_usdt: number;
  total_withdrawal_usdt: number;
};

interface DepositTransaction {
  uid: number | string;
  date: string;
  txtid: string;
  amount: number;
  coin: string;
}

interface LiveBet {
  game: string;
  uid: number;
  amount: number;
  coin: string;
  name: string;
}

interface WithdrawalTransaction {
  uid: number | string;
  date: string;
  wallet: string;
  amount: number;
  coin: string;
  name: string | null;
  profit: number;
}

const LiveBets = () => {
  const [userCount, setUserCount] = useState(0);
  const [totalDeposits, setTotalDeposits] = useState(0);
  const [totalWithdrawals, setTotalWithdrawals] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [todayDeposits, setTodayDeposits] = useState(0);
  const [todayWithdrawals, setTodayWithdrawals] = useState(0);
  const [liveBets, setLiveBets] = useState<LiveBet[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [transactions, setTransactions] = useState<Array<DepositTransaction | WithdrawalTransaction>>([]);

  const [chartData1, setChartData] = useState({
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: 'indigo',
    }]
  });

  const [pieData, setPieData] = useState({
    labels: ['Crash', 'Mines', 'Other Games'],
    datasets: [{
      data: [0, 0, 0],
      backgroundColor: ['#4338ca', '#818cf8', '#c7d2fe'],
    }]
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userResponse = await fetch('https://api.trubetx.com/users');
        const userData = await userResponse.json();
        setUsers(userData);
        setUserCount(userData.length);

        const totalDepositsResponse = await fetch('https://api.trubetx.com/total-deposits');
        const totalDepositsData = await totalDepositsResponse.json();
        setTotalDeposits(totalDepositsData.total_deposits_usdt);

        const totalWithdrawalsResponse = await fetch('https://api.trubetx.com/total-withdrawals');
        const totalWithdrawalsData = await totalWithdrawalsResponse.json();
        setTotalWithdrawals(totalWithdrawalsData.total_withdrawals_usdt);

      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };

    const fetchTodayDepositsAndWithdrawals = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];

        const depositResponse = await fetch('https://api.trubetx.com/today-deposits');
        const depositData = await depositResponse.json();

        const totalDepositAmountToday = depositData.reduce((total: any, deposit: { date: string; amount: any; }) => {
          const depositDate = deposit.date.split('T')[0];
          return depositDate === today ? total + deposit.amount : total;
        }, 0);
        setTodayDeposits(totalDepositAmountToday);

        const withdrawResponse = await fetch('https://api.trubetx.com/today-withdrawals');
        const withdrawData = await withdrawResponse.json();

        const totalWithdrawalAmountToday = withdrawData.reduce((total: any, withdraw: { date: string; amount: any; }) => {
          const withdrawDate = withdraw.date.split('T')[0];
          return withdrawDate === today ? total + withdraw.amount : total;
        }, 0);
        setTodayWithdrawals(totalWithdrawalAmountToday);

      } catch (error) {
        console.error('Failed to fetch deposit or withdrawal data:', error);
      }
    };

    fetchTodayDepositsAndWithdrawals();
    fetchUserData();
  }, []);

  const fetchTodayDeposits = async () => {
    const response = await fetch('https://api.trubetx.com/today-deposits');
    const data: DepositTransaction[] = await response.json();
    const today = new Date().toISOString().split('T')[0];
    const todayDeposits = data.filter(deposit => new Date(deposit.date).toISOString().split('T')[0] === today);
    setTransactions(todayDeposits);
  };

  const fetchTodayWithdrawals = async () => {
    const response = await fetch('https://api.trubetx.com/today-withdrawals');
    const data: WithdrawalTransaction[] = await response.json();
    const today = new Date().toISOString().split('T')[0];
    const todayWithdrawals = data.filter(withdrawal => new Date(withdrawal.date).toISOString().split('T')[0] === today);
    setTransactions(todayWithdrawals);
  };


  const [page, setPage] = useState(1);
    const [limit] = useState(10); // or make it dynamic if needed
    const [totalPages, setTotalPages] = useState(1);
    

  const fetchLiveBets = async (page, limit) => {
    try {
      const response = await fetch(`https://api.trubetx.com/live-bets?page=${page || 1}&limit=${limit || 10}`);
      const data: LiveBet[] = await response.json();
  
      // Check if data is an array
      if (Array.isArray(data?.data)) {
        setLiveBets(data?.data);
        setTotalPages(data?.pagination?.totalPages || 1); // Set total pages from response
        setPage(data?.pagination?.currentPage || 1); // Set current page from response
      } else {
        console.error('Fetched data is not an array');
        setLiveBets([]); // Set empty array if data is not valid
      }
    } catch (error) {
      console.error('Failed to fetch live bets:', error);
      setLiveBets([]); // Set empty array in case of error
    }
  };
  

  useEffect(() => {
    fetchLiveBets(page, limit);
    const interval = setInterval(fetchLiveBets, 10000);
    return () => clearInterval(interval);
  }, [page, limit]);

  useEffect(() => {
    // Update chart data when liveBets changes

    // setChartData({
    //   labels: Object.keys(gameData),
    //   datasets: [{
    //     data: Object.values(gameData),
    //     backgroundColor: 'indigo',
    //   }]
    // });

    // Update pie chart data
    
    const crashBets = liveBets.filter(bet => bet.game === 'crash').reduce((sum, bet) => sum + bet.amount, 0);
    const mineBets = liveBets.filter(bet => bet.game === 'mine').reduce((sum, bet) => sum + bet.amount, 0);
    const otherBets = liveBets.filter(bet => bet.game !== 'crash' && bet.game !== 'mine').reduce((sum, bet) => sum + bet.amount, 0);

    setPieData({
      labels: ['Crash', 'Mines', 'Other Games'],
      datasets: [{
        data: [crashBets, mineBets, otherBets],
        backgroundColor: ['#4338ca', '#818cf8', '#c7d2fe'],
      }]
    });
  }, [liveBets]);


  const gameData = {
    Mine: 400000,
    Crash: 600000,
    Plinko: 1480000,
    High_Low: 700000,
    Hash_Dice: 100000,
    SingleKeno: 20000,
    Hilo: 10000
  };

  const chartData = {
    labels: Object.keys(gameData),
    datasets: [{
      data: Object.values(gameData),
      backgroundColor: '#394a6a', // Indigo color
      barThickness: 40,
    }]
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        // text: 'Live Bets by Game',
        font: {
          size: 18,
          weight: 'bold' as const,

        },
        padding: {
          top: 10,
          bottom: 30,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: 'black',  // This line changes the x-axis label color to white
        },
        border: {
          color: 'black'  // This changes the x-axis line color to white
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          color: 'black',  // This line changes the text color to white
          callback: function (value) {
            return (value as number).toLocaleString();
          },
          stepSize: 200000,
          // max: 1600000,
        },
        border: {
          color: 'black'  // This changes the x-axis line color to white
        }
      },
    },
  };
  return (
    <div className='ml-0 p-3 lg:p-0 lg:ml-[256px] h-screen '>
      <h1 className="text-3xl font-bold mb-2 text-[#292524]">Live Bets </h1>
   

      
      <div className='flex justify-center items-start h-screen mt-5'>
        <div className='w-full h-[450px]  rounded-lg'>
          <table className="text-sm text-left text-gray-500 w-full">
            <thead className="text-m text-white uppercase bg-[#394a6a] top-0 z-5">
              <tr>
                <th scope="col" className="py-3 px-6">Live Bets</th>
                <th scope="col" className="py-3 px-6"></th>
                <th scope="col" className="py-3 px-6"></th>
                <th scope="col" className="py-3 px-6"></th>
                <th scope="col" className="py-3 px-6"></th>
                <th scope="col" className="py-3 px-6"></th>
                <th scope="col" className="py-3 px-6"></th>
              </tr>
            </thead>
            <thead className="text-xs text-white uppercase bg-gray-400   top-[3rem] z-10">
              <tr>
                <th scope="col" className="py-3 px-6">S.No</th>
                <th scope="col" className="py-3 px-6">Name</th>
                <th scope="col" className="py-3 px-6">Uid</th>
                <th scope="col" className="py-3 px-6">Game</th>
                <th scope="col" className="py-3 px-6">Bet</th>
                <th scope="col" className="py-3 px-6">Profit</th>
                <th scope="col" className="py-3 px-6">Coin</th>
              </tr>
            </thead>
            <tbody>
            {liveBets.length > 0 ? (
              liveBets.reverse().map((bet, index) => (
                <tr key={index} className="group bg-[#FFFFF] border-b border-gray-200 hover:bg-gray-50">
                  <td className="text-black group-hover:text-gray-600 py-4 px-6">{index + 1}</td>
                  <td className="text-black group-hover:text-gray-600 py-4 px-6">{bet.name}</td>
                  <td className="text-black group-hover:text-gray-600 py-4 px-6">{bet.uid}</td>
                  <td className="text-black group-hover:text-gray-600 py-4 px-6">{bet.game}</td>
                  <td className="text-black group-hover:text-gray-600 py-4 px-6">${parseFloat(bet.amount.toString()).toFixed(8)}</td>
                 <td className={`py-4 px-6 ${parseFloat(bet?.profit) > 0 ? "text-green-600" : "text-red-600"}`}>
                  ${parseFloat(bet?.profit.toString()).toFixed(8)}
                </td>
                  <td className="text-black group-hover:text-gray-600 py-4 px-6">{"USD"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-4 px-6">No live bets available</td>
              </tr>
            )}
          </tbody>

           <tfoot>
                <tr>
                  <td colSpan={16} className="py-4 px-6 text-start">
                    <nav className="inline-flex items-center space-x-2">
                      <button
                    onClick={() => fetchLiveBets(page - 1, limit)}
                    disabled={page <= 1}
                    className={`px-4 py-2 rounded ${
                      page <= 1
                        ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    }`}
                  >
                    Previous
                      </button>
                      <span className="text-gray-700 px-4 py-2">Page {page} of {totalPages}</span>
                      <button
                        onClick={() => fetchLiveBets(page + 1, limit)}
                        disabled={page >= totalPages}
                        className={`px-4 py-2 rounded ${
                          page >= totalPages
                            ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                            : "bg-blue-500 text-white hover:bg-blue-600"
                        }`}
                      >
                        Next
                      </button>
                    </nav>
                  </td>
                </tr>
              </tfoot>


          </table>
        </div>
      </div>
    </div>
  );
};

export default LiveBets;
